/**
 * FLUXOS DE ONBOARDING:
 * 
 * OFICIAL (usar sempre):
 * - /onboarding → Onboarding.tsx (usa Smartfile internamente)
 *   Steps internos: Conceito → Cadastro Paciente → Upload → Resultados
 * 
 * DEPRECATED (manter temporariamente para usuários que já iniciaram):
 * - /onboarding/miafile → OnboardingMIAFILE.tsx (DESCONTINUADO)
 * - /onboarding/register → Register.tsx
 * - /onboarding/patient → Patient.tsx
 * - /onboarding/upload → Upload.tsx
 * - /onboarding/analysis → Analysis.tsx
 */

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getFriendlyErrorMessage } from "@/lib/errors";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AdminSidebar } from "@/components/AdminSidebar";
import { AppLayout } from "@/components/layout/AppLayout";
import { HeartPulse, Search } from "lucide-react";
import { AdminBreadcrumbs } from "@/components/admin/AdminBreadcrumbs";
import { CommandPalette } from "@/components/admin/CommandPalette";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { useState } from "react";
import { DiagnosticsBadge } from "@/components/DiagnosticsBadge";
import { MobileNavProvider } from "@/contexts/MobileNavContext";
import { ModalProvider } from "@/contexts/ModalContext";
import { CameraProvider } from "@/components/ui/camera/camera-provider";
import { SubscriptionProvider } from "@/components/subscription/SubscriptionProvider";
import { DataStaleProvider } from "@/contexts/DataStaleContext";
import { ScrollToTop } from "@/components/ScrollToTop";
import PoliticaPrivacidade from "./pages/PoliticaPrivacidade";
import TermosUso from "./pages/TermosUso";
import MealPlanExtension from "./pages/extensions/mealplan";
import MealPlanList from "./pages/extensions/mealplan/MealPlanList";
import MealPlanCreate from "./pages/extensions/mealplan/MealPlanCreate";
import MealPlanDetailView from "./pages/extensions/mealplan/MealPlanDetailView";
import MealTrackingPublic from "./pages/public/MealTrackingPublic";
import PilatesPlannerExtension from "./pages/extensions/pilatesplanner";
import PilatesPlanDetail from "./pages/extensions/pilatesplanner/PilatesPlanDetail";
import PrescriptionList from "./pages/extensions/prescription/PrescriptionList";
import PrescriptionCreate from "./pages/extensions/prescription/PrescriptionCreate";
import PrescriptionDetail from "./pages/extensions/prescription/PrescriptionDetail";
import ClinicalRecordList from "./pages/extensions/clinicalrecord/ClinicalRecordList";
import ClinicalRecordCreate from "./pages/extensions/clinicalrecord/ClinicalRecordCreate";
import ClinicalRecordDetail from "./pages/extensions/clinicalrecord/ClinicalRecordDetail";
import Auth from "./pages/Auth";
import ConfirmEmail from "./pages/auth/ConfirmEmail";
import EmailVerification from "./pages/auth/EmailVerification";
import ResetPassword from "./pages/auth/ResetPassword";
import OnboardingRegister from "./pages/onboarding/Register";
import OnboardingNew from "./pages/onboarding/OnboardingNew";
import { OnboardingProvider } from "./contexts/OnboardingContext";
// import OnboardingMIAFILE from "./pages/onboarding/OnboardingMIAFILE"; // DEPRECATED - arquivo removido
import Patient from "./pages/onboarding/Patient";
import Upload from "./pages/onboarding/Upload";
import Analysis from "./pages/onboarding/Analysis";
import Dashboard from "./pages/app/Dashboard";
import ChatIA from "./pages/app/ChatIA";
import Pacientes from "./pages/app/Pacientes";
import PacientesImportar from "./pages/app/PacientesImportar";
import PacienteDetalhes from "./pages/app/PacienteDetalhes";
import PacienteEditar from "./pages/app/PacienteEditar";
import PacienteRelatorio from "./pages/app/PacienteRelatorio";
import PacienteRelatorioPDF from "./pages/app/PacienteRelatorioPDF";
import PacienteResultados from "./pages/app/PacienteResultados";
import Estatisticas from "./pages/app/Estatisticas";
import MinhaConta from "./pages/app/MinhaConta";
import Suporte from "./pages/app/Suporte";
import Categorias from "./pages/app/Categorias";
import Aplicativos from "./pages/app/Aplicativos";
import AplicativoView from "./pages/app/AplicativoView";
import AdminDashboard from "./pages/admin/AdminDashboard";
import Profissionais from "./pages/admin/Profissionais";
import ProfissionalDetails from "./pages/admin/ProfissionalDetails";
import PacientesAdmin from "./pages/admin/PacientesAdmin";
import PacienteDetails from "./pages/admin/PacienteDetails";
import Assinaturas from "./pages/admin/Assinaturas";
import Planos from "./pages/admin/Planos";
import TabelasPreco from "./pages/admin/TabelasPreco";
import Pagamentos from "./pages/admin/Pagamentos";
import Configuracoes from "./pages/admin/Configuracoes";
import ConfiguracoesGerais from "./pages/admin/ConfiguracoesGerais";
import ConfiguracoesUsuarios from "./pages/admin/ConfiguracoesUsuarios";
import ConfiguracoesIAs from "./pages/admin/ConfiguracoesIAs";
import ConfiguracoesPagarme from "./pages/admin/ConfiguracoesPagarme";
import ContentTypesConfig from "./pages/admin/ContentTypesConfig";
import ConfiguracoesAplicativos from "./pages/admin/ConfiguracoesAplicativos";
import ConfiguracoesEmails from "./pages/admin/ConfiguracoesEmails";
import Templates from "./pages/admin/Templates";
import SiteCases from "./pages/admin/SiteCases";
import SiteBlog from "./pages/admin/SiteBlog";
import NotFound from "./pages/NotFound";
import ProtectedRoute from "./components/ProtectedRoute";
import { OnboardingRoute } from "./components/OnboardingRoute";
import SmartfileWizard from "./pages/smartfile/SmartfileWizard";

// ======================================================
// QueryClient com captura de erros do Sentry
// ======================================================
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (import.meta.env.DEV) console.log({ failureCount, error })

        if (import.meta.env.DEV) return false
        if (failureCount > 3) return false

        // Não retry em erros de autenticação
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as any).status;
          if ([401, 403].includes(status)) return false;
        }

        return true;
      },
      refetchOnWindowFocus: import.meta.env.PROD,
      staleTime: 10 * 1000,
    },
    mutations: {
      onError: (error) => {
        // Captura erros de API no Sentry
        Sentry.captureException(error, {
          tags: { errorType: 'react-query-mutation' },
          extra: {
            errorMessage: error?.message,
            errorStack: error?.stack,
          },
        });

        // Exibe mensagem amigável ao usuário
        const friendlyMessage = getFriendlyErrorMessage(error);
        toast.error(friendlyMessage);
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      // Captura erros do React Query
      Sentry.captureException(error, {
        tags: { errorType: 'react-query' },
        extra: {
          errorMessage: error?.message,
          errorStack: error?.stack,
        },
      });

      // Tratamento de erro 401 (sessão expirada)
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as any).status;
        if (status === 401) {
          toast.error('Sessão expirada. Por favor, faça login novamente.');
          supabase.auth.signOut().then(() => {
            const redirect = encodeURIComponent(window.location.pathname + window.location.search);
            window.location.href = redirect ? `/auth?redirect=${redirect}` : '/auth';
          });
          return; // Não exibe outro toast
        }
      }

      // Exibe mensagem amigável para outros erros
      const friendlyMessage = getFriendlyErrorMessage(error);
      toast.error(friendlyMessage);
    },
  }),
});

import PatientsList from "./pages/patients/PatientsList";
import StatsDashboard from "./pages/stats/StatsDashboard";
import DocumentView from "./pages/documents/DocumentView";
import SearchResults from "./pages/search/SearchResults";
import SharedPatientView from "./pages/public/SharedPatientView";
import AssistedCollectionPublic from "./pages/public/AssistedCollectionPublic";

const AdminLayout = () => {
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  useKeyboardShortcuts(() => setCommandPaletteOpen(true));

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AdminSidebar />
        <div className="flex-1 flex flex-col md:ml-64 md:peer-data-[state=collapsed]:ml-12 transition-[margin] duration-200">
          <header className="h-14 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-40 flex items-center px-4 gap-4">
            <SidebarTrigger />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-card rounded-lg flex items-center justify-center">
                <HeartPulse className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold">Admin</span>
            </div>

            <div className="hidden md:block mx-4">
              <AdminBreadcrumbs />
            </div>

            <button
              onClick={() => setCommandPaletteOpen(true)}
              className="ml-auto hidden md:flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground border rounded-md hover:bg-muted/50 transition"
            >
              <Search className="h-4 w-4" />
              <span>Buscar...</span>
              <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                <span className="text-xs">⌘</span>K
              </kbd>
            </button>
          </header>
          <main className="flex-1 p-6">
            <Routes>
              <Route path="/" element={<AdminDashboard />} />
              <Route path="/cadastros/profissionais" element={<Profissionais />} />
              <Route path="/cadastros/profissionais/:id" element={<ProfissionalDetails />} />
              <Route path="/cadastros/pacientes" element={<PacientesAdmin />} />
              <Route path="/cadastros/pacientes/:id" element={<PacienteDetails />} />
              <Route path="/site/cases" element={<SiteCases />} />
              <Route path="/site/blog" element={<SiteBlog />} />
              <Route path="/assinaturas" element={<Assinaturas />} />
              <Route path="/planos" element={<Planos />} />
              <Route path="/tabelas-preco" element={<TabelasPreco />} />
              <Route path="/pagamentos" element={<Pagamentos />} />
              <Route path="/configuracoes" element={<Configuracoes />} />
              <Route path="/configuracoes/gerais" element={<ConfiguracoesGerais />} />
              <Route path="/configuracoes/usuarios" element={<ConfiguracoesUsuarios />} />
              <Route path="/configuracoes/ias" element={<ConfiguracoesIAs />} />
              <Route path="/configuracoes/pagarme" element={<ConfiguracoesPagarme />} />
              <Route path="/configuracoes/tipos-conteudo" element={<ContentTypesConfig />} />
              <Route path="/configuracoes/aplicativos" element={<ConfiguracoesAplicativos />} />
              <Route path="/configuracoes/emails" element={<ProtectedRoute requiredRole={["admin"]}><ConfiguracoesEmails /></ProtectedRoute>} />
              <Route path="/templates" element={<Templates />} />
            </Routes>
          </main>
        </div>
      </div>
      <CommandPalette open={commandPaletteOpen} onOpenChange={setCommandPaletteOpen} />
    </SidebarProvider>
  );
};

const App = () => (
  <Sentry.ErrorBoundary
    fallback={({ error, resetError }) => (
      <div
        style={{
          padding: 24,
          display: 'grid',
          placeItems: 'center',
          minHeight: '100vh',
          textAlign: 'center',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <div style={{ maxWidth: 720 }}>
          <h2 style={{ fontSize: 22, marginBottom: 12 }}>
            Ops! Algo deu errado 😵
          </h2>

          <p style={{ color: '#666', marginBottom: 18 }}>
            Ocorreu um erro inesperado na interface. Nossa equipe foi notificada.
          </p>

          <pre
            style={{
              whiteSpace: 'pre-wrap',
              background: '#f4f4f4',
              padding: '1rem',
              borderRadius: 8,
              textAlign: 'left',
              overflow: 'auto',
              fontSize: 12,
            }}
          >
            {error instanceof Error ? error.message : String(error)}
          </pre>

          <div style={{ marginTop: 18, display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              onClick={() => resetError()}
              style={{
                background: '#0ea5e9',
                color: 'white',
                padding: '10px 16px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Tentar novamente
            </button>

            <button
              onClick={() => window.location.reload()}
              style={{
                background: '#111827',
                color: 'white',
                padding: '10px 16px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Recarregar página
            </button>
          </div>
        </div>
      </div>
    )}
  >
    <QueryClientProvider client={queryClient}>
      <CameraProvider>
      <MobileNavProvider>
        <ModalProvider>
          <SubscriptionProvider>
            <DataStaleProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <DiagnosticsBadge />
              <BrowserRouter>
                <ScrollToTop />
            <Routes>
            <Route path="/" element={<Navigate to="/auth" replace />} />
            <Route path="/politica-privacidade" element={<PoliticaPrivacidade />} />
            <Route path="/termos-uso" element={<TermosUso />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/confirm-email" element={<ConfirmEmail />} />
            <Route path="/auth/verify-email" element={<EmailVerification />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />

            {/* Onboarding routes */}
            {/* <Route path="/onboarding/miafile" element={<OnboardingMIAFILE />} /> */}

            {/* DEPRECATED: Manter temporariamente para usuários que já iniciaram */}
            <Route path="/onboarding" element={
              <OnboardingRoute>
                <OnboardingProvider>
                  <OnboardingNew />
                </OnboardingProvider>
              </OnboardingRoute>
            } />
            <Route path="/onboarding/register" element={
              <OnboardingRoute>
                <OnboardingRegister />
              </OnboardingRoute>
            } />
            <Route path="/onboarding/patient" element={
              <OnboardingRoute>
                <Patient />
              </OnboardingRoute>
            } />
            <Route path="/onboarding/upload" element={
              <OnboardingRoute>
                <Upload />
              </OnboardingRoute>
            } />
            <Route path="/onboarding/analysis" element={
              <OnboardingRoute>
                <Analysis />
              </OnboardingRoute>
            } />

            {/* Shared patient view - public */}
            <Route path="/compartilhar/:token" element={<SharedPatientView />} />

            {/* Assisted collection - public */}
            <Route path="/coleta/:token" element={<AssistedCollectionPublic />} />

            {/* Public meal tracking */}
            <Route path="/refeicao/:token" element={<MealTrackingPublic />} />

            <Route
              path="/app/*"
              element={
                <ProtectedRoute requiredRole={["clinician"]}>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="pacientes" element={<PatientsList />} />
              <Route path="pacientes/importar" element={<PacientesImportar />} />
              <Route path="pacientes/:id" element={<PacienteDetalhes />} />
              <Route path="pacientes/:id/editar" element={<PacienteEditar />} />
              <Route path="pacientes/:id/relatorio" element={<PacienteRelatorio />} />
              <Route path="pacientes/:id/relatorio-pdf" element={<PacienteRelatorioPDF />} />
              <Route path="pacientes/:id/resultados" element={<PacienteResultados />} />
              <Route path="documentos/:documentId" element={<DocumentView />} />
              <Route path="busca" element={<SearchResults />} />
              <Route path="categorias" element={<Categorias />} />
              <Route path="aplicativos" element={<Aplicativos />} />
              <Route path="aplicativos/:slug" element={<AplicativoView />} />
              <Route path="estatisticas" element={<Estatisticas />} />
              <Route path="minha-conta" element={<MinhaConta />} />
              <Route path="minha-conta/:tab" element={<MinhaConta />} />
              <Route path="suporte" element={<Suporte />} />
              <Route path="chat-ia" element={<ChatIA />} />
              <Route path="aplicativo/mealplan" element={<MealPlanList />} />
              <Route path="aplicativo/mealplan/novo" element={<ProtectedRoute requiredRole={["clinician"]}><MealPlanCreate /></ProtectedRoute>} />
              <Route path="aplicativo/mealplan/:planId" element={<MealPlanDetailView />} />
              <Route path="aplicativo/pilatesplanner" element={<PilatesPlannerExtension />} />
              <Route path="aplicativo/pilatesplanner/:planId" element={<PilatesPlanDetail />} />
              <Route path="aplicativo/prescription" element={<PrescriptionList />} />
              <Route path="aplicativo/prescription/novo" element={<ProtectedRoute requiredRole={["clinician"]}><PrescriptionCreate /></ProtectedRoute>} />
              <Route path="aplicativo/prescription/:prescriptionId" element={<PrescriptionDetail />} />
              <Route path="aplicativo/clinicalrecord" element={<ClinicalRecordList />} />
              <Route path="aplicativo/clinicalrecord/novo" element={<ProtectedRoute requiredRole={["clinician"]}><ClinicalRecordCreate /></ProtectedRoute>} />
              <Route path="aplicativo/clinicalrecord/:id" element={<ClinicalRecordDetail />} />
              <Route path="aplicativo/clinicalrecord/:id/editar" element={<ProtectedRoute requiredRole={["clinician"]}><ClinicalRecordCreate /></ProtectedRoute>} />
              {/* <Route path="miafile" element={<MIAFILEWizard />} /> */}
              <Route path="smartfile" element={<SmartfileWizard />} />
            </Route>

            <Route
              path="/admin/*"
              element={
                <ProtectedRoute requiredRole={["admin"]}>
                  <AdminLayout />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
            </BrowserRouter>
            </TooltipProvider>
            </DataStaleProvider>
            </SubscriptionProvider>
          </ModalProvider>
        </MobileNavProvider>
      </CameraProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
